import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaThumbtack, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import { Message } from '../../types';
import PinnedMessageItem from './PinnedMessageItem';
import { useToast } from '@/components/common/ToastProvider';
import socket from '../../services/socket';

interface PinnedMessagesPanelProps {
  channelId: string;
  onClose: () => void;
  onUnpin?: (messageId: string) => void;
  onMessageClick?: (messageId: string) => void;
}

// Cache for pinned messages with longer TTL
const pinnedMessagesCache = new Map<string, { messages: Message[]; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minute cache

const PinnedMessagesPanel: React.FC<PinnedMessagesPanelProps> = ({
  channelId,
  onClose,
  onUnpin,
  onMessageClick,
}) => {
  const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const { error: toastError } = useToast();
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef<boolean>(false); // Prevent concurrent requests
  const listRef = useRef<HTMLDivElement | null>(null);

  const fetchPinnedMessages = useCallback(
    async (forceRefresh: boolean = false) => {
      try {
        // Prevent concurrent requests
        if (isFetchingRef.current && !forceRefresh) {
          return;
        }

        // Check cache first
        const cached = pinnedMessagesCache.get(channelId);
        if (cached && !forceRefresh && Date.now() - cached.timestamp < CACHE_DURATION) {
          setPinnedMessages(cached.messages);
          setLoading(false);
          return;
        }

        isFetchingRef.current = true;
        setLoading(true);

        const response = await api.get(`/messages/channels/${channelId}/pinned`);
        let messages = response.data.messages || [];

        // Deduplicate messages by ID to prevent duplicate key warnings
        const seenIds = new Set<string>();
        messages = messages.filter((msg: Message) => {
          const idKey = String(msg.id);
          if (seenIds.has(idKey)) {
            return false;
          }
          seenIds.add(idKey);
          return true;
        });

        // Update cache
        pinnedMessagesCache.set(channelId, {
          messages,
          timestamp: Date.now(),
        });

        setPinnedMessages(messages);
      } catch (error: any) {
        console.error('Error fetching pinned messages:', error);
        toastError('Failed to load pinned messages');
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    },
    [channelId, toastError],
  );

  // Handle message pinned event via Socket.io with debouncing
  const handleMessagePinned = useCallback(
    (data: any) => {
      if (data.channelId === channelId) {
        // Debounce fetch to prevent rate limiting
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }
        fetchTimeoutRef.current = setTimeout(() => {
          fetchPinnedMessages(true);
        }, 1000); // Wait 1 second before fetching to consolidate rapid events
      }
    },
    [channelId, fetchPinnedMessages],
  );

  // Handle message unpinned event via Socket.io with debouncing
  const handleMessageUnpinned = useCallback(
    (data: any) => {
      if (data.channelId === channelId) {
        // Debounce fetch to prevent rate limiting
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }
        fetchTimeoutRef.current = setTimeout(() => {
          fetchPinnedMessages(true);
        }, 1000); // Wait 1 second before fetching to consolidate rapid events
      }
    },
    [channelId, fetchPinnedMessages],
  );

  // Handle local unpin action
  const handleUnpin = useCallback(
    async (messageId: string) => {
      const { success: toastSuccess, error: toastError } = useToast();
      try {
        // Call API to unpin
        await api.delete(`/messages/${messageId}/pin`);
        toastSuccess('Message unpinned');

        // Remove from local state immediately
        setPinnedMessages((prev) => prev.filter((msg) => String(msg.id) !== String(messageId)));
        // Invalidate cache
        pinnedMessagesCache.delete(channelId);

        if (onUnpin) {
          onUnpin(messageId);
        }
      } catch (error: any) {
        console.error('Error unpinning message:', error);
        const errorMessage = error.response?.data?.error || 'Failed to unpin message';
        toastError(errorMessage);
      }
    },
    [channelId, onUnpin],
  );

  useEffect(() => {
    // Initial fetch
    fetchPinnedMessages();
    setFocusedIdx(0);

    // Listen for real-time pin/unpin events
    socket.on('message:pinned', handleMessagePinned);
    socket.on('message:unpinned', handleMessageUnpinned);

    // Cleanup
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      socket.off('message:pinned', handleMessagePinned);
      socket.off('message:unpinned', handleMessageUnpinned);
    };
  }, [channelId, fetchPinnedMessages, handleMessagePinned, handleMessageUnpinned]);

  useEffect(() => {
    if (pinnedMessages.length > 0) {
      setFocusedIdx(0);
    }
  }, [pinnedMessages]);

  const focusPinned = (idx: number) => {
    const container = listRef.current;
    if (!container) return;
    const items = Array.from(container.querySelectorAll<HTMLElement>('[data-pinned-item]'));
    const el = items[idx];
    if (el) {
      el.focus();
      setFocusedIdx(idx);
    }
  };

  const handlePinnedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      (listRef.current || document).querySelectorAll<HTMLElement>('[data-pinned-item]'),
    );
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement;
    let idx = items.findIndex((el) => el === active);
    if (idx === -1) idx = focusedIdx;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        focusPinned((idx + 1) % items.length);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        focusPinned((idx - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        focusPinned(0);
        break;
      case 'End':
        e.preventDefault();
        focusPinned(items.length - 1);
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        items[idx].click();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-80 sm:w-96 bg-brand-surface/95 dark:bg-[#19163e]/95 backdrop-blur-xl shadow-2xl z-40 flex flex-col border-l border-brand-outline-variant/60 animate-m3-slide-down">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-brand-outline-variant/60 bg-brand-surface dark:bg-[#19163e]">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-primary-600/20 text-primary-400 rounded-full">
            <FaThumbtack size={16} />
          </div>
          <h2 className="text-lg font-bold text-brand-on-background dark:text-[#e0e7ff]">
            Pinned Messages
          </h2>
        </div>
        <button
          onClick={onClose}
          aria-label="Close pinned messages"
          className="p-2 hover:bg-white/10 rounded-full transition-all text-brand-outline hover:text-white active:scale-95 cursor-pointer"
          title="Close"
        >
          <FaTimes size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" ref={listRef} onKeyDown={handlePinnedKeyDown}>
        {loading ? (
          <div className="flex items-center justify-center h-full animate-pulse">
            <div className="text-center">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-brand-outline-variant dark:border-brand-outline-variant border-t-primary-600 rounded-full mb-3"></div>
              <p className="text-brand-outline dark:text-brand-on-surface-variant text-sm font-medium">
                Loading pinned messages...
              </p>
            </div>
          </div>
        ) : pinnedMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-4">
              <div className="text-4xl mb-3 opacity-30">📌</div>
              <p className="text-brand-outline dark:text-brand-on-surface-variant text-sm font-medium">
                No pinned messages yet
              </p>
              <p className="text-brand-on-surface-variant dark:text-brand-outline text-xs mt-2">
                Pin important messages to see them here
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 p-4">
            {pinnedMessages.map((message, index) => (
              <div
                key={message.id}
                data-pinned-item
                tabIndex={focusedIdx === index ? 0 : -1}
                onFocus={() => setFocusedIdx(index)}
                className="animate-in fade-in slide-in-from-bottom-2 duration-300 outline-none"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <PinnedMessageItem
                  message={message}
                  onUnpin={handleUnpin}
                  onClick={onMessageClick}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PinnedMessagesPanel;
